import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BlogCard from "./BlogCard";
import ApprovalBlogCard from "./ApprovalBlogCard";
import { useAuth } from "../Auth/AuthContext";

const BlogSection = () => {
    const { authState } = useAuth();
    const role = authState.roles;
    const user = authState.user;
    const admin = role === "Admin";
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: false
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    // Content for unapproved users
    const unApprovedContent = {
        title: "You are not approved yet",
        excerpt: "Hey, thank you for joining us! Please wait for approval. Our team will review and approve your access shortly.",
        image: "./Images/PendingApproval.png",
        author: "Adspace",
        date: "",
        readMoreLink: "https://adspace-tech.net/",
    };

    // Blogs List
    const blogs = [
        {
            title: "Maximizing ROI with Google Ads: Best Practices",
            excerpt: "Discover how to optimize your Google Ads campaigns to maximize return on investment (ROI) with expert tips and strategies...",
            image: "./Images/Roi-google.jpg",
            author: "Alice Carter",
            date: "October 16, 2024",
            readMoreLink: "https://adspace-tech.net/",
        },
        {
            title: "Understanding Google AdX: A Complete Guide",
            excerpt: "Google AdX offers advanced programmatic advertising solutions. Learn how it differs from Google Ads and how it benefits publishers...",
            image: "./Images/Google-Adx.png",
            author: "Mark Reynolds",
            date: "October 10, 2024",
            readMoreLink: "https://adspace-tech.net/",
        },
        {
            title: "Google Ads vs. Google AdX: Which is Right for Your Business?",
            excerpt: "Choosing between Google Ads and Google AdX can be tricky. This article breaks down the differences and helps you decide what's best for your business...",
            image: "./Images/googlevsadx.png",
            author: "Samantha Lee",
            date: "October 8, 2024",
            readMoreLink: "https://adspace-tech.net/",
        },
        {
            title: "How to Improve Ad Quality Score in Google Ads",
            excerpt: "Improving your Google Ads Quality Score can lead to lower costs and better ad performance. Learn key factors that influence this score...",
            image: "./Images/imporveadsquality.jpg",
            author: "David Collins",
            date: "October 5, 2024",
            readMoreLink: "https://adspace-tech.net/",
        },
        {
            title: "Monetizing Your Website with Google AdX",
            excerpt: "If you're a publisher looking to maximize revenue, Google AdX offers advanced tools for programmatic advertising. Here's how to get started...",
            image: "./Images/monitizewebsite.png",
            author: "Emily Johnson",
            date: "October 2, 2024",
            readMoreLink: "https://adspace-tech.net/",
        },
    ];

    const contactBlog = {
        title: "Get in Touch with Us",
        excerpt: "In today's digital world, staying connected is more important than ever. Whether you have questions, need assistance, or want to explore collaboration opportunities, our team is just a message away!",
        image: "./Images/microsoftteams.png",
        author: "Adspace Admin",
        date: new Date().toDateString(),
        readMoreLink: "https://teams.live.com/l/invite/FEAeu3E6yc8ggyIYAQ",
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    return (
        <div className="relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden z-0">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-purple-100 opacity-20"
                        style={{
                            width: Math.random() * 200 + 50,
                            height: Math.random() * 200 + 50,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            y: [0, Math.random() * 100 - 50],
                            x: [0, Math.random() * 100 - 50],
                            opacity: [0.1, 0.3, 0.1],
                        }}
                        transition={{
                            duration: Math.random() * 20 + 10,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "easeInOut",
                        }}
                    />
                ))}
            </div>

            <motion.div
                ref={ref}
                className="container mx-auto px-4 py-16 relative z-10"
                initial="hidden"
                animate={controls}
                variants={containerVariants}
            >
                {/* Hero section */}
                <motion.div 
                    className="text-center mb-16"
                    variants={{
                        hidden: { y: -50, opacity: 0 },
                        visible: {
                            y: 0,
                            opacity: 1,
                            transition: { duration: 0.8 }
                        }
                    }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                        Publisher Resources
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Expert insights to help you maximize your ad revenue and optimize your publishing strategy.
                    </p>
                </motion.div>

                {/* Featured card (contact) */}
                <motion.div
                    className="flex justify-center mb-16"
                    variants={{
                        hidden: { scale: 0.9, opacity: 0 },
                        visible: {
                            scale: 1,
                            opacity: 1,
                            transition: { 
                                duration: 0.6,
                                type: "spring",
                                stiffness: 100
                            }
                        }
                    }}
                >
                    <div className="w-full max-w-4xl">
                        <BlogCard {...contactBlog} featured />
                    </div>
                </motion.div>

                {/* Blog grid */}
                {admin || user.isApprovedByAdmin ? (
                    <motion.div 
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                        variants={containerVariants}
                    >
                        {blogs.map((blog, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                whileHover={{ 
                                    y: -5,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <BlogCard {...blog} />
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <motion.div
                        className="flex justify-center"
                        variants={{
                            hidden: { scale: 0.95, opacity: 0 },
                            visible: {
                                scale: 1,
                                opacity: 1,
                                transition: { 
                                    duration: 0.6,
                                    type: "spring"
                                }
                            }
                        }}
                    >
                        <ApprovalBlogCard {...unApprovedContent} />
                    </motion.div>
                )}

                {/* Floating CTA */}
                <motion.div
                    className="fixed bottom-8 right-8 z-50"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <a
                        href="#contact"
                        className="bg-gradient-to-r from-purple-600 to-blue-500 text-white py-3 px-6 rounded-full shadow-lg flex items-center"
                    >
                        <span className="mr-2">Need Help?</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                    </a>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default BlogSection;